import { useEffect, useRef, useState } from "react";

import chevronDown from "../../assets/svg/chevron-down.svg";
import { Product as ProductType } from "../../types/Product";
import Product from "./Product.tsx";

type DropdownOption = {
  key?: string | number;
  label: string;
  value: string | number;
};

interface DropdownLargeProps {
  options: DropdownOption[] | ProductType[];
  label: string;
  onChange?: (
    value: string | number | null,
    isSelected: boolean,
    values?: number[],
  ) => void;
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
  selected?: number | null;
  selectedProducts?: number[];
  error?: boolean;
}

/**
 * DropdownLarge renders a large dropdown with a floating label that changes size and position
 * based on the dropdown state (default, open, answered).
 * It supports both regular options and Product options, displaying products in a 2-column grid when provided.
 * For products, the dropdown stays open on selection and shows a blue border for selected items.
 * Products can be toggled on/off by clicking them again.
 */
const DropdownLarge: React.FC<DropdownLargeProps> = ({
  options,
  label,
  onChange,
  multiple = false,
  disabled = false,
  required = false,
  selected = null,
  selectedProducts = [],
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    DropdownOption | ProductType | null
  >(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if options are products
  const isProductOptions = options.length > 0 && "name" in options[0];

  // Update selectedOption when selected prop changes
  useEffect(() => {
    if (multiple) {
      return;
    }

    const option = options.find(
      (opt) => opt.value.toString() === selected?.toString(),
    );
    setSelectedOption(option ?? null);
  }, [selected, options, isProductOptions, multiple]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Determine label state
  const isFloating =
    !!selectedOption ||
    isOpen ||
    (isProductOptions && selectedProducts.length > 0);
  const isSmall = !!selectedOption && !isOpen;

  const getDisplayLabel = (option: DropdownOption | ProductType | null) => {
    if (!option) return "";
    return "name" in option ? option.name : option.label;
  };

  const handleOptionSelect = (option: DropdownOption | ProductType | null) => {
    if (option === null) {
      setSelectedOption(null);
      setIsOpen(false);
      onChange?.(null, false);
      return;
    }

    if (multiple) {
      const isAlreadySelected = selectedProducts.includes(Number(option.value));
      onChange?.(option.value, !isAlreadySelected);
    } else {
      setSelectedOption(option);
      onChange?.(option.value, true);
    }

    if (!multiple && !(option as ProductType).entry?.entry_customer_quantity) {
      setIsOpen(false);
    }
  };

  return (
    <div
      className={`relative rounded-t-xl w-full font-montserrat ${required && !selectedOption ? "bg-red-100" : "bg-gray-100"}`}
      ref={dropdownRef}
    >
      {/* Floating label */}
      <span
        className={`absolute left-4 transition-all duration-200 pointer-events-none
                    ${isFloating ? "top-2 text-xs text-gray-500" : "top-1/2 -translate-y-1/2 text-lg text-gray-900"}
                    ${isSmall ? "text-xs" : "text-lg"}
                `}
        style={{ zIndex: 2 }}
      >
        {label}
        {required && <span className="text-red-500 ms-1">*</span>}
      </span>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full ${label.length > 50 ? "min-h-[100px]" : ""} p-4 pt-7 text-lg bg-transparent appearance-none border-0 border-b-2 ${
          error && required ? "border-red-500" : "border-black"
        } focus:outline-none ${
          error && required ? "focus:border-red-500" : "focus:border-black"
        } text-gray-900 text-left flex items-center justify-between ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        disabled={disabled}
        aria-label={label}
      >
        {/* Show selected option or nothing if floating, otherwise show label */}
        <span
          className={`transition-all duration-200 ${isFloating ? "opacity-100" : "opacity-0"}`}
        >
          {multiple
            ? `${selectedProducts.length} selected`
            : getDisplayLabel(selectedOption)}
        </span>
        <img
          src={chevronDown}
          alt="chevron-down"
          className={`w-6 h-6 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`absolute left-0 right-0 bg-gray-100 transition-all duration-300 ease-in-out z-15 ${
          isOpen
            ? "max-h-[500px] opacity-100 overflow-y-auto shadow-xl"
            : "max-h-0 opacity-0"
        }`}
        style={{ top: "100%" }}
      >
        {isOpen &&
          (isProductOptions ? (
            <>
              {!required && (
                <button
                  className={`w-full px-4 py-3 text-left text-lg hover:bg-gray-200 transition-colors duration-150`}
                  onClick={() => handleOptionSelect(null)}
                >
                  [Clear Selection]
                </button>
              )}
              <div className="grid grid-cols-2 gap-4 p-4 pt-1">
                {(options as ProductType[]).map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleOptionSelect(product)}
                    className={`w-full hover:bg-gray-200 transition-colors duration-150 rounded-lg ${
                      selectedProducts.includes(product.id) ||
                      selected === product.id
                        ? "border-2 border-primary"
                        : "border-2 border-transparent"
                    }`}
                  >
                    <Product product={product} />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              {!required && (
                <button
                  className={`w-full px-4 py-3 text-left text-lg hover:bg-gray-200 transition-colors duration-150 ${
                    !selectedOption ? "bg-gray-200" : ""
                  }`}
                  onClick={() => handleOptionSelect(null)}
                >
                  [Clear Selection]
                </button>
              )}
              {(options as DropdownOption[]).map((option) => (
                <button
                  key={option.key || option.value.toString()}
                  onClick={() => handleOptionSelect(option)}
                  className={`w-full px-4 py-3 text-left text-lg hover:bg-gray-200 transition-colors duration-150 ${
                    selectedOption &&
                    "value" in selectedOption &&
                    selectedOption.value.toString() === option.value.toString()
                      ? "bg-gray-200"
                      : ""
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </>
          ))}
      </div>
    </div>
  );
};

export default DropdownLarge;
